import {ConnectedRouter} from 'connected-react-router';
import "index.scss";
import 'network/security-interceptor';
import 'network/sso-interceptor';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import configureStore, { history } from 'state/store/configureStore';
import App from './components/App';
import QuestionnaireWidget from './components/widgets/questionnaire-widget';
import MedicationWidget from "./components/widgets/medication-widget";
import { CustomerTestimonialWidget } from "./components/widgets/customer-testimonial-widget";
import { store as medicationWidgetStore } from "./state/store/configureMedWidgetStore";
import MedicationSummaryWidget from 'components/widgets/medication-summary-carousel-widget';
import { emitMarkersForWidget } from 'utils';
import { ConditionPickerWidget, ConditionPickerWidgetProps } from 'components/widgets/condition-picker-widget';
import TwisterTileWidget from "./components/widgets/twister-tile-widget";
import { ConditionCarouselWidget } from 'components/widgets/condition-carousel-widget';

// Comment out if you want the requests to hit your RDE instance.
if (process.env.NODE_ENV === 'development') {
    require('mocks/handlers.js');
}

const render = () => {
    const RootHtml = () => {
        const store = configureStore();
        return (
            <Provider store={store}>
                <ConnectedRouter history={history}>
                    <App/>
                </ConnectedRouter>
            </Provider>
        );
    };

    document.querySelectorAll('[data-hcr-dynamic-widget-id]').forEach((element)=>{
        if (!(element instanceof HTMLElement)) {
            return;
        }

        // Condition carousel widget
        if (element.dataset.hcrDynamicWidgetId === 'kyanite-sf-condition-carousel-widget'){
            const items = JSON.parse(element.dataset.items ?? '[]');
            const stretch = element.dataset.stretch === 'true';
            ReactDOM.render(<ConditionCarouselWidget
                items={items}
                stretch={stretch}/>, element, () =>
                emitMarkersForWidget(element)
            );
        }

        // Condition picker widget
        if (element.dataset.hcrDynamicWidgetId === 'kyanite-sf-condition-picker-widget') {
            const widgetattr = JSON.parse(element.dataset.widgetattr ?? '{}');
            const props: ConditionPickerWidgetProps = { ...widgetattr, stretchHead: true }
            ReactDOM.render(<ConditionPickerWidget
                {...props} />, element, () =>
                emitMarkersForWidget(element)
            );
        }

        // Twister tile widget
        if (element.dataset.hcrDynamicWidgetId === 'kyanite-cdp-twister-tile-widget') {
            const widgetAttributes = JSON.parse(element.dataset.widgetattr ?? "{}");
            ReactDOM.render(<TwisterTileWidget {...widgetAttributes}/>, element, () =>
                emitMarkersForWidget(element)
            )
        }

        // please keep adding widget below...
    });

    // New generation questionnaire warmup widget
    const questionnaireWarmupElementId = 'clinic-questionnaire-warmup-forms-widget';
    const questionnaireWarmupElement = document.getElementById(questionnaireWarmupElementId);
    if (questionnaireWarmupElement && questionnaireWarmupElement.dataset.reactInitialized !== "true") {
        ReactDOM.render(<QuestionnaireWidget
            questionnaireWidgetAttributesJson={questionnaireWarmupElement.dataset.widgetAttr}
        />, questionnaireWarmupElement);

        // use reactInitialized = true/false to check if this React component has been initialized.
        questionnaireWarmupElement.dataset.reactInitialized = "true";
    }

    // New generation medication widget
    // TODO:To be removed after FF launch 3 as carousel was just a style and we are using new token names, https://issues.amazon.com/issues/KATARA-SX-6393
    const medicationElementId = 'clinic-medication-carousel-cards-widget';
    const medicationElement = document.getElementById(medicationElementId);
    if (medicationElement && medicationElement.dataset.reactInitialized !== "true") {
        ReactDOM.render(<Provider store={medicationWidgetStore}>
            <MedicationWidget/>
        </Provider>, medicationElement, () => emitMarkersForWidget(medicationElement));

        // use reactInitialized = true/false to check if this React component has been initialized.
        medicationElement.dataset.reactInitialized = "true";
    }

    // Medication price estimation widget
    const medicationPriceEstimationContainerElementId = 'clinic-medication-price-estimation-widget';
    const medicationPriceEstimationContainerElement = document.getElementById(medicationPriceEstimationContainerElementId);
    if (medicationPriceEstimationContainerElement) {
        ReactDOM.render(<Provider store={medicationWidgetStore}>
            <MedicationWidget/>
        </Provider>, medicationPriceEstimationContainerElement, () => emitMarkersForWidget(medicationPriceEstimationContainerElement));
    }

    // Medication summary carousel widget
    const medicationSummaryContainerElementId = 'clinic-medication-summary-carousel-widget';
    const medicationSummaryContainerElement = document.getElementById(medicationSummaryContainerElementId);
    if (medicationSummaryContainerElement) {
        ReactDOM.render(< MedicationSummaryWidget />, medicationSummaryContainerElement, () => emitMarkersForWidget(medicationSummaryContainerElement));
    }

    // Customer testimonial widget
    const customerTestimonialWidgetId = 'clinic-customer-testimonial-widget';
    const customerTestimonialWidgetElement = document.getElementById(customerTestimonialWidgetId);
    if (customerTestimonialWidgetElement) {
        ReactDOM.render(<CustomerTestimonialWidget />, customerTestimonialWidgetElement, () => emitMarkersForWidget(customerTestimonialWidgetElement));
    }

    // Legacy cdp buy box react root.
    const cdpBuyBoxComponent = document.getElementById('clinic-CDP-buy-box-react-root')
    if (cdpBuyBoxComponent) {
        ReactDOM.render(<RootHtml/>, cdpBuyBoxComponent);
    }
};

render();
